import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme-toggle";
import { RegisterForm, LoginCard, useAuth } from "@/components/auth";
import { AdminDashboard } from "@/components/admin/admin-dashboard";
import { OnboardingForm, AnnotationInterface } from "@/components/annotation";
import { ExamplesViewer } from "@/components/examples-viewer";
import { isAdminUser } from "@/utils/admin";
import { LogOut, Shield, Settings, Headphones } from "lucide-react";
import { toast } from "sonner";
import "./index.css";

import logo from "./logo.svg";

function AuthenticatedContent() {
  const { user, logout, lastSyncEvent } = useAuth();
  const [currentView, setCurrentView] = useState<'home' | 'admin' | 'annotate'>('home');
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [creatingSubset, setCreatingSubset] = useState(false);

  const isAdmin = isAdminUser(user);

  // Check onboarding status on mount
  React.useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const response = await fetch('/api/onboarding/status', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setOnboardingDone(data.completed);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, []);

  const handleCreateSubset = async () => {
    // Check if onboarding is done before creating subset
    if (!onboardingDone) {
      toast.info('Please complete onboarding first');
      setCurrentView('annotate');
      return;
    }

    setCreatingSubset(true);
    try {
      const response = await fetch('/api/samples/create-subset', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to create subset');
      }

      const data = await response.json();
      
      if (data.assigned === 0) {
        if (data.existing && data.existing > 0) {
          // User already has assigned samples
          toast.info(data.message);
          setCurrentView('annotate');
        } else {
          // No more samples available
          toast.info(data.message);
        }
      } else {
        toast.success(data.message);
        setCurrentView('annotate');
      }
    } catch (error) {
      console.error('Error creating subset:', error);
      toast.error('Failed to create annotation subset');
    } finally {
      setCreatingSubset(false);
    }
  };

  if (currentView === 'admin' && isAdmin) {
    return (
      <div className="w-full">
        <div className="mb-4">
          <Button variant="outline" onClick={() => setCurrentView('home')} className="mb-4">
            ← Back to Home
          </Button>
        </div>
        <AdminDashboard />
      </div>
    );
  }

  if (currentView === 'annotate') {
    if (!onboardingDone) {
      return (
        <div className="w-full max-w-2xl mx-auto">
          <div className="mb-4">
            <Button variant="outline" onClick={() => setCurrentView('home')} className="mb-4">
              ← Back to Home
            </Button>
          </div>
          <OnboardingForm onComplete={() => {
            setOnboardingDone(true);
            setCurrentView('home');
            toast.success('Onboarding complete! Now click "Annotate Subset" to start.');
          }} />
        </div>
      );
    }
    return (
      <div className="w-full max-w-2xl mx-auto">
        <div className="mb-4">
          <Button variant="outline" onClick={() => setCurrentView('home')} className="mb-4">
            ← Back to Home
          </Button>
        </div>
        <AnnotationInterface />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-center items-center gap-8 mb-6">
        <img
          src={logo}
          alt="Emolia Logo"
          className="h-24 p-4 transition-all duration-300 hover:drop-shadow-[0_0_2em_#646cffaa]"
        />
      </div>

      <Card className="bg-card/50 backdrop-blur-sm border-muted max-w-xl mx-auto">
        <CardContent className="pt-6">
          <h1 className="text-4xl font-bold my-3 leading-tight">Emolia Annotation</h1>
          <div className="mb-5 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-green-600 dark:text-green-400" />
              <span className="font-semibold text-green-800 dark:text-green-200">
                Welcome, {user?.username}!
              </span>
              {isAdmin && (
                <span className="ml-2 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                  Admin
                </span>
              )}
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-6">
            You will be presented with short audio clips drawn from multiple languages and asked to evaluate the presence of a specific emotion in each recording. For every sample, please indicate whether the target emotion is absent, weakly present, or strongly present.
            Your judgments contribute to a large-scale dataset used to advance research in affective computing and cross-linguistic emotion recognition. Each session comprises 8,000 samples. Please listen carefully to each clip before responding.
          </p>

          <div className="flex flex-col gap-3 items-center">
            <Button
              onClick={handleCreateSubset}
              className="gap-2 w-full max-w-xs"
              variant="default"
              size="lg"
              disabled={creatingSubset || checkingOnboarding}
            >
              <Headphones className="h-5 w-5" />
              {checkingOnboarding ? 'Loading...' : creatingSubset ? 'Creating Subset...' : 'Annotate Subset'}
            </Button>

            {isAdmin && (
              <Button
                onClick={() => setCurrentView('admin')}
                className="gap-2 w-full max-w-xs"
                variant="outline"
              >
                <Settings className="h-4 w-4" />
                Admin Dashboard
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function UnauthenticatedContent() {
  const { login } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');

  const handleAuthSuccess = (user: { id: string; username: string; isAdmin?: boolean }, token: string) => {
    login(user, token);
  };

  const handleAuthError = (error: string) => {
    console.error('Authentication error:', error);
  };

  return (
    <>
      <div className="flex justify-center items-center gap-8 mb-6">
        <img
          src={logo}
          alt="Emolia Logo"
          className="h-24 p-4 transition-all duration-300 hover:drop-shadow-[0_0_2em_#646cffaa]"
        />
      </div>

      <Card className="bg-card/50 backdrop-blur-sm border-muted mb-8 max-w-xl mx-auto">
        <CardContent className="pt-6">
          <h1 className="text-4xl font-bold my-3 leading-tight">Emolia Annotation</h1>
          <p className="mb-4 text-muted-foreground">
            Audio emotion annotation platform. Sign in to start annotating.
          </p>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Use Touch ID, Face ID, or a security key to authenticate.</p>
          </div>
        </CardContent>
      </Card>

      {mode === 'login' ? (
        <div className="space-y-4 max-w-md mx-auto">
          <LoginCard onSuccess={handleAuthSuccess} onError={handleAuthError} />

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <button
                onClick={() => setMode('register')}
                className="text-primary hover:underline font-medium"
              >
                Create one
              </button>
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4 max-w-md mx-auto">
          <RegisterForm onSuccess={handleAuthSuccess} onError={handleAuthError} />

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <button
                onClick={() => setMode('login')}
                className="text-primary hover:underline font-medium"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export function App() {
  const { user, logout } = useAuth();

  // Check if we're on the /examples route
  if (window.location.pathname === '/examples') {
    return (
      <div className="min-h-screen relative z-10">
        <div className="absolute top-4 right-4 flex gap-2">
          <ThemeToggle />
        </div>
        <ExamplesViewer />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8 text-center relative z-10">
      {/* Theme toggle and logout in top right corner */}
      <div className="absolute top-4 right-4 flex gap-2">
        <ThemeToggle />
        {user && (
          <Button variant="outline" size="icon" onClick={logout} title="Sign Out">
            <LogOut className="h-4 w-4" />
          </Button>
        )}
      </div>

      {user ? <AuthenticatedContent /> : <UnauthenticatedContent />}
    </div>
  );
}

export default App;
